import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ProvisioningService } from './services/provisioning-service/provisioning-service';
import { DeviceSnapshotsService } from './services/device-snapshots.service';
import { AddDeviceDialogComponent } from './add-provisioned-device/add-provisioned-device.component';
import { AssignContentDialogComponent } from './assign-content-dialog/assign-content-dialog.component';
import { DeviceProvisioningResponse } from './models/device-provisioning-response.model';
import { DeviceSnapshot } from './models/device-snapshot.model';
import { ContentBinding, LocationName } from './models/device-provisioning-request.model';
import { PageableApiResponse } from '../api-response';
import { ToastService } from '../../shared/services/toast-service';
import { LanguageService } from '../../core/i18n/language.service';
import { Grid } from '../../shared/components/grid/grid';
import { GridPage } from '../../shared/components/grid/grid-page';
import { GridDetailDirective } from '../../shared/components/grid/directives/grid-detail.directive';
import { GridColumn, GridRowAction, GridSort, GridStatus, gridColumns } from '../../shared/components/grid/models/grid.model';
import { Button } from '../../shared/components/button/button';
import { Spinner } from '../../shared/components/spinner/spinner';

type DeviceRow = DeviceProvisioningResponse;

@Component({
  selector: 'app-provisioned-devices',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    DatePipe,
    Grid,
    GridPage,
    Spinner,
    GridDetailDirective,
    AddDeviceDialogComponent,
    AssignContentDialogComponent,
    Button,
  ],
  templateUrl: './provisioned-devices.component.html',
})
export class ProvisionedDevices implements OnInit {
  private readonly provisioningService = inject(ProvisioningService);
  private readonly deviceSnapshotsService = inject(DeviceSnapshotsService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly devices = signal<DeviceRow[]>([]);
  readonly page = signal(0);
  readonly size = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);
  readonly sort = signal<GridSort>({ sortBy: 'created', sortDirection: 'DESC' });
  readonly isLoading = signal(false);

  /** The device whose detail panel is open (drives the dialogs' target too). */
  readonly selectedDevice = signal<DeviceRow | null>(null);
  readonly isAddDialogOpen = signal(false);
  readonly isSavingDevice = signal(false);
  readonly isAssignContentDialogOpen = signal(false);
  /** The device pending removal — non-null drives the confirmation modal. */
  readonly devicePendingRemoval = signal<DeviceRow | null>(null);
  readonly isRemoving = signal(false);

  /** Latest live image (snapshot) for the currently open detail panel. */
  readonly latestSnapshot = signal<DeviceSnapshot | null>(null);
  /** Most recent successfully uploaded snapshot — shown in place of
   *  `latestSnapshot` while a newer one is still PENDING/FAILED, so the
   *  drawer doesn't fall back to a bare loading placeholder. */
  readonly lastUploadedSnapshot = signal<DeviceSnapshot | null>(null);
  readonly displaySnapshot = computed(() =>
    this.latestSnapshot()?.status === 'UPLOADED' ? this.latestSnapshot() : this.lastUploadedSnapshot(),
  );
  readonly latestSnapshotImageUrl = signal<string | null>(null);
  readonly isLoadingLatestSnapshot = signal(false);

  readonly deviceId = (d: DeviceRow) => d.clientId;
  readonly asDevice = (row: unknown): DeviceRow => row as DeviceRow;

  /**
   * Columns as a computed so labels AND status-pill labels re-translate on a
   * language switch (it depends on LanguageService.current()). Labels are
   * resolved here through the `devices` scope — not the grid's `labelKey`, which
   * resolves against the grid's own scope, not `devices.*`.
   */
  readonly columns = computed<GridColumn<DeviceRow>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`devices.columns.${k}`);
    return gridColumns<DeviceRow>([
      { key: 'clientId', label: t('clientId'), type: 'text', textVariant: 'strong', sortable: true, alwaysVisible: true, width: 220 },
      { key: 'online', label: t('connectivity'), type: 'status', sortable: false, width: 150, status: (_v, row) => this.connectivityStatus(row) },
      { key: 'serialNumber', label: t('serialNumber'), type: 'text', sortable: true },
      { key: 'type', label: t('type'), type: 'text', sortable: true, width: 130 },
      { key: 'vendorId', label: t('vendorId'), type: 'text', sortable: true },
      { key: 'status', label: t('status'), type: 'status', sortable: false, width: 170, status: (_v, row) => this.deviceStatus(row) },
      { key: 'created', label: t('created'), type: 'date', sortable: true, width: 180 },
    ]);
  });

  ngOnInit(): void {
    this.loadDevices();
  }

  private loadDevices(): void {
    const { sortBy, sortDirection } = this.sort();
    this.isLoading.set(true);
    this.provisioningService
      .getProvisionedDevices(this.page(), this.size(), sortBy, sortDirection)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: PageableApiResponse<DeviceProvisioningResponse>) => {
          this.devices.set(data.data);
          this.page.set(data.page);
          this.size.set(data.size);
          this.totalElements.set(data.totalElements);
          this.totalPages.set(data.totalPages);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching devices:', error);
          this.toastService.show('Failed to load devices', 'error');
          this.isLoading.set(false);
        },
      });
  }

  /** Online/offline connectivity pill, driven by the API's `online` flag. */
  private connectivityStatus(device: DeviceRow): GridStatus {
    const t = (k: string) => this.transloco.translate(`devices.status.${k}`);
    return device.online
      ? { label: t('online'), color: 'green' }
      : { label: t('offline'), color: 'red' };
  }

  /** Derives the status pill from provisioning state (translated). */
  private deviceStatus(device: DeviceRow): GridStatus {
    const t = (k: string) => this.transloco.translate(`devices.status.${k}`);
    if (device.waitingForReset) {
      return { label: t('awaitingReset'), color: 'orange' };
    }
    if (device.isProvisioned) {
      return { label: t('provisioned'), color: 'green' };
    }
    return { label: t('unprovisioned'), color: 'neutral', hollow: true };
  }

  onSortChange(sort: GridSort): void {
    this.sort.set(sort);
    this.page.set(0);
    this.loadDevices();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.loadDevices();
  }

  onPageSizeChange(size: number): void {
    this.size.set(size);
    this.page.set(0);
    this.loadDevices();
  }

  onRowClick(device: DeviceRow): void {
    this.selectedDevice.set(device);
    this.loadLatestSnapshot(device.clientId);
  }

  /** Double-click opens the full device details page (devices/:clientId). */
  onRowDblClick(device: DeviceRow): void {
    this.router.navigate([device.clientId], {
      relativeTo: this.activatedRoute,
      state: { device },
    });
  }

  /**
   * Row actions: Edit inline, the rest under the ⋮ overflow menu. Labels are
   * translated here (not via the grid's `labelKey`, which resolves against the
   * grid's own i18n scope, not `devices.*`). Re-reads on language change.
   */
  readonly rowActions = computed<GridRowAction<DeviceRow>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`devices.actions.${k}`);
    return [
      { id: 'edit', label: t('edit'), icon: 'icon-edit-pencil', placement: 'inline' },
      { id: 'assignContent', label: t('assignContent'), icon: 'icon-menu-medialibrary1', placement: 'menu' },
      { id: 'status', label: t('status'), icon: 'icon-view-eye', placement: 'menu' },
      { id: 'reset', label: t('reset'), icon: 'icon-disable-ban', placement: 'menu', danger: true },
      { id: 'remove', label: t('remove'), icon: 'icon-delete-trash', placement: 'menu', danger: true },
    ];
  });

  /** Dispatches a row action: target the action's row, then run it. */
  onRowAction(actionId: string, device: DeviceRow): void {
    this.selectedDevice.set(device);
    this.loadLatestSnapshot(device.clientId);
    switch (actionId) {
      case 'edit':
        this.editSelectedDevice();
        break;
      case 'assignContent':
        this.openAssignContentDialog();
        break;
      case 'status':
        this.requestStatusForSelectedDevice();
        break;
      case 'reset':
        this.resetSelectedDevice();
        break;
      case 'remove':
        this.openRemoveModal(device);
        break;
    }
  }

  /** The stops cell/detail value, formatted compactly as the bindings' resource (stop) ids. */
  localitiesText(device: DeviceRow): string {
    const stops = device.contentBindings?.map((b) => b.resourceId) ?? [];
    return stops.length ? stops.join(', ') : this.transloco.translate('common.na');
  }

  // ── Add / edit dialog ─────────────────────────────────────────────────

  openAddDeviceDialog(): void {
    this.selectedDevice.set(null);
    this.isAddDialogOpen.set(true);
  }

  editSelectedDevice(): void {
    if (this.selectedDevice()) this.isAddDialogOpen.set(true);
  }

  closeAddDeviceDialog(): void {
    this.isAddDialogOpen.set(false);
  }

  handleDeviceAdded(newDevice: {
    clientId: string;
    contentBindings: ContentBinding[];
    latitude: number | null;
    longitude: number | null;
    locationName: LocationName | null;
    deviceConfigurationId: number | null;
    mode: 'add' | 'edit';
  }): void {
    const payload = {
      clientId: newDevice.clientId,
      contentBindings: newDevice.contentBindings,
      latitude: newDevice.latitude,
      longitude: newDevice.longitude,
      locationName: newDevice.locationName,
      deviceConfigurationId: newDevice.deviceConfigurationId,
    };

    this.isSavingDevice.set(true);

    if (newDevice.mode === 'edit') {
      // PUT still only returns a boolean (not confirmed changed like POST),
      // so this path still has to fall back to a full refetch.
      this.provisioningService
        .putProvisioningForDevice(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.isSavingDevice.set(false);
            this.toastService.show('Device updated successfully', 'success');
            this.closeAddDeviceDialog();
            this.loadDevices();
          },
          error: () => {
            this.isSavingDevice.set(false);
            this.toastService.show('Error updating device', 'error');
            this.closeAddDeviceDialog();
          },
        });
      return;
    }

    // POST now returns the created device DTO, so the list can be updated
    // directly from it — no need to refetch the (not immediately consistent)
    // list endpoint just to show the device that was just created.
    this.provisioningService
      .postProvisioningForDevice(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSavingDevice.set(false);
          this.toastService.show('Device added successfully', 'success');
          this.closeAddDeviceDialog();
          this.devices.update((list) => [response.data, ...list]);
          this.totalElements.update((n) => n + 1);
        },
        error: () => {
          this.isSavingDevice.set(false);
          this.toastService.show('Error adding device', 'error');
          this.closeAddDeviceDialog();
        },
      });
  }

  // ── Detail-panel actions (Set Image / Reset / Status) ─────────────────

  /** Fetches the device's latest live image for the open detail panel.
   *  The snapshots API is keyed by numeric deviceId, not clientId, so the
   *  heartbeat is looked up first to resolve it. */
  private loadLatestSnapshot(clientId: string): void {
    this.latestSnapshot.set(null);
    this.lastUploadedSnapshot.set(null);
    this.latestSnapshotImageUrl.set(null);
    this.isLoadingLatestSnapshot.set(true);
    this.provisioningService
      .getHeartbeatForDevice(clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (heartbeat) => {
          const deviceId = heartbeat.data?.deviceId;
          if (deviceId == null) {
            this.isLoadingLatestSnapshot.set(false);
            return;
          }
          this.deviceSnapshotsService
            .getLatest(deviceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response) => {
                const snapshot = response.data ?? null;
                this.latestSnapshot.set(snapshot);
                this.isLoadingLatestSnapshot.set(false);
                if (snapshot?.status === 'UPLOADED') {
                  this.loadSnapshotImage(snapshot);
                } else {
                  this.loadLastUploadedSnapshot(deviceId);
                }
              },
              error: () => this.isLoadingLatestSnapshot.set(false),
            });
        },
        error: () => this.isLoadingLatestSnapshot.set(false),
      });
  }

  /** Finds the newest UPLOADED snapshot to show while the true latest is
   *  still pending/failed. Fetches a small, unfiltered page since the last
   *  successful upload may be older than the last few requests. */
  private loadLastUploadedSnapshot(deviceId: number): void {
    this.deviceSnapshotsService
      .list(deviceId, 0, 5)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const uploaded = (response.data ?? [])
            .filter((s) => s.status === 'UPLOADED')
            .sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
          const snapshot = uploaded[0] ?? null;
          this.lastUploadedSnapshot.set(snapshot);
          if (snapshot) {
            this.loadSnapshotImage(snapshot);
          }
        },
      });
  }

  private loadSnapshotImage(snapshot: DeviceSnapshot): void {
    this.deviceSnapshotsService
      .getDownloadUrl(snapshot.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (download) => this.latestSnapshotImageUrl.set(download?.url ?? null),
      });
  }

  openAssignContentDialog(): void {
    if (this.selectedDevice()) this.isAssignContentDialogOpen.set(true);
  }

  resetSelectedDevice(): void {
    const device = this.selectedDevice();
    if (!device) return;
    this.provisioningService
      .postMqttResetForDevice(device, 'FULL')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.toastService.show('MQTT Reset request sent successfully', 'success'),
        error: (error) => {
          console.error('Error sending MQTT Reset request:', error);
          this.toastService.show('Error sending MQTT Reset request', 'error');
        },
      });
  }

  // ── Remove device ─────────────────────────────────────────────────────

  openRemoveModal(device: DeviceRow): void {
    this.devicePendingRemoval.set(device);
  }

  closeRemoveModal(): void {
    if (this.isRemoving()) return;
    this.devicePendingRemoval.set(null);
  }

  confirmRemove(): void {
    const device = this.devicePendingRemoval();
    if (!device || this.isRemoving()) return;
    this.isRemoving.set(true);
    this.provisioningService
      .deleteDevice(device.clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('devices.toast.removeSuccess'), 'success');
          this.isRemoving.set(false);
          this.devicePendingRemoval.set(null);
          // Drop the open detail panel if it was showing the removed device.
          if (this.selectedDevice()?.clientId === device.clientId) {
            this.selectedDevice.set(null);
          }
          this.loadDevices();
        },
        error: (error) => {
          console.error('Error removing device:', error);
          this.toastService.show(this.transloco.translate('devices.toast.removeFailed'), 'error');
          this.isRemoving.set(false);
        },
      });
  }

  requestStatusForSelectedDevice(): void {
    const device = this.selectedDevice();
    if (!device) return;
    this.provisioningService
      .getMqttStatusForDevice(device)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.toastService.show('MQTT Status request sent successfully', 'success'),
        error: (error) => {
          console.error('Error sending MQTT Status request:', error);
          this.toastService.show('Error sending MQTT Status request', 'error');
        },
      });
  }
}
